import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Alert,
    Box,
    Button,
    Chip,
    Container,
    Divider,
    Grid,
    InputAdornment,
    List,
    ListItemButton,
    ListItemText,
    MenuItem,
    Paper,
    Stack,
    TextField,
    Typography
} from '@mui/material';
import {
    AccountBalanceWallet as WalletIcon,
    Add as AddIcon,
    Class as ClassIcon,
    History as HistoryIcon,
    Person as PersonIcon,
    Save as SaveIcon
} from '@mui/icons-material';
import styled, { keyframes } from 'styled-components';
import axios from 'axios';
import { getAllSclasses } from '../../../redux/sclassRelated/sclassHandle';
import { getAllStudents } from '../../../redux/studentRelated/studentHandle';
import { useToast } from '../../../context/ToastContext';
import { CustomNepaliDatePicker, getCurrentBSDate, formatDatePart } from '../../../utils/customDate';

const fadeIn = keyframes`from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); }`;

const AdvanceFees = () => {
    const dispatch = useDispatch();
    const { currentUser } = useSelector((state) => state.user);
    const { sclassesList } = useSelector((state) => state.sclass);
    const { studentsList } = useSelector((state) => state.student);
    const { showToast } = useToast();

    const [selectedClass, setSelectedClass] = useState('');
    const [studentId, setStudentId] = useState('');
    const [amount, setAmount] = useState('');
    const [date, setDate] = useState(getCurrentBSDate());
    const [note, setNote] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!currentUser?._id) return;
        dispatch(getAllSclasses(currentUser._id, 'Sclass'));
        dispatch(getAllStudents(currentUser._id));
    }, [currentUser, dispatch]);

    const classStudents = useMemo(() => {
        if (!selectedClass) return [];
        return (studentsList || []).filter((student) => {
            const classRef = student.sclassName;
            const classId = typeof classRef === 'object' ? classRef?._id : classRef;
            return classId === selectedClass;
        });
    }, [studentsList, selectedClass]);

    const selectedStudent = useMemo(
        () => (studentsList || []).find((student) => student._id === studentId) || null,
        [studentsList, studentId]
    );

    const transactions = useMemo(() => {
        return [...(selectedStudent?.advanceTransactions || [])].sort((a, b) => {
            const aTime = new Date(a.createdAt || 0).getTime();
            const bTime = new Date(b.createdAt || 0).getTime();
            return bTime - aTime;
        });
    }, [selectedStudent]);

    const handleClassSelect = (classId) => {
        setSelectedClass(classId);
        setStudentId('');
    };

    const handleSave = async () => {
        const parsedAmount = Number(amount);

        if (!studentId) {
            showToast('Please select a student first.', 'warning');
            return;
        }
        if (!parsedAmount || parsedAmount <= 0) {
            showToast('Please enter a valid advance amount.', 'warning');
            return;
        }

        setSaving(true);
        try {
            await axios.post(`${process.env.REACT_APP_BASE_URL}/Student/${studentId}/Advance`, {
                amount: parsedAmount,
                date,
                note: note || 'Advance fee received',
                schoolId: currentUser._id
            });

            setAmount('');
            setNote('');
            setDate(getCurrentBSDate());
            await dispatch(getAllStudents(currentUser._id));
            showToast('Advance payment added successfully.', 'success');
        } catch (error) {
            showToast(error.response?.data?.message || 'Failed to add advance payment.', 'error');
        } finally {
            setSaving(false);
        }
    };

    return (
        <PageContainer>
            <HeaderWrapper elevation={0}>
                <Container maxWidth="xl">
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 4 }}>
                        <Box>
                            <Typography variant="h4" sx={{ fontWeight: 800, color: 'white', mb: 1 }}>
                                Advance Fees
                            </Typography>
                            <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.72)' }}>
                                Add student advance payments and review available balances
                            </Typography>
                        </Box>
                        <WalletIcon sx={{ color: 'rgba(255,255,255,0.85)', fontSize: 44 }} />
                    </Stack>
                </Container>
            </HeaderWrapper>

            <Container maxWidth="xl" sx={{ mt: 2, height: 'calc(100vh - 120px)', overflow: 'hidden', pb: 2 }}>
                <Grid container spacing={3} sx={{ height: '100%' }}>
                    <Grid item xs={12} md={4} sx={{ height: '100%' }}>
                        <StyledPaper elevation={3}>
                            <SectionHeader>
                                <ClassIcon />
                                <Typography variant="h6" fontWeight="700">Classes</Typography>
                            </SectionHeader>
                            <Box sx={{ p: 2, flex: 1, overflowY: 'auto' }}>
                                <Stack spacing={1}>
                                    {(sclassesList || []).map((sclass) => (
                                        <ClassItem
                                            key={sclass._id}
                                            $active={selectedClass === sclass._id}
                                            onClick={() => handleClassSelect(sclass._id)}
                                            startIcon={<ClassIcon />}
                                        >
                                            {sclass.sclassName}
                                        </ClassItem>
                                    ))}
                                </Stack>
                                {(!sclassesList || sclassesList.length === 0) && (
                                    <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', py: 4 }}>
                                        No classes found.
                                    </Typography>
                                )}
                                <Alert severity="info" sx={{ mt: 2, borderRadius: '12px', fontSize: '0.75rem' }}>
                                    Select a class, then choose a student to add advance fees.
                                </Alert>
                            </Box>
                        </StyledPaper>
                    </Grid>

                    <Grid item xs={12} md={8} sx={{ height: '100%', overflowY: 'auto', pr: 1 }}>
                        <StyledPaper elevation={3}>
                            <SectionHeader>
                                <WalletIcon />
                                <Typography variant="h6" fontWeight="700">
                                    Advance Payment {selectedClass && `( ${sclassesList?.find(c => c._id === selectedClass)?.sclassName || ''} )`}
                                </Typography>
                            </SectionHeader>
                            <Box sx={{ p: 3 }}>
                                {!selectedClass ? (
                                    <Box sx={{ py: 10, textAlign: 'center' }}>
                                        <Typography color="textSecondary">Please select a class to manage advance fees.</Typography>
                                    </Box>
                                ) : (
                                    <Grid container spacing={3}>
                                        <Grid item xs={12}>
                                            <TextField
                                                select
                                                fullWidth
                                                label="Student"
                                                value={studentId}
                                                onChange={(e) => setStudentId(e.target.value)}
                                                InputProps={{
                                                    startAdornment: <InputAdornment position="start"><PersonIcon sx={{ color: '#2563eb' }} /></InputAdornment>
                                                }}
                                            >
                                                {classStudents.map((student) => (
                                                    <MenuItem key={student._id} value={student._id}>
                                                        {student.name} {student.rollNum ? `(Roll: ${student.rollNum})` : ''}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        </Grid>

                                        {selectedStudent && (
                                            <Grid item xs={12}>
                                                <BalanceBox>
                                                    <Box>
                                                        <Typography variant="caption" sx={{ color: '#1d4ed8', fontWeight: 800 }}>
                                                            CURRENT ADVANCE BALANCE
                                                        </Typography>
                                                        <Typography variant="h4" sx={{ color: '#1e3a8a', fontWeight: 900 }}>
                                                            Rs.{(Number(selectedStudent.advanceBalance) || 0).toLocaleString()}
                                                        </Typography>
                                                    </Box>
                                                    <Chip
                                                        icon={<HistoryIcon />}
                                                        label={`${transactions.length} transaction${transactions.length === 1 ? '' : 's'}`}
                                                        sx={{ fontWeight: 800, bgcolor: '#dbeafe', color: '#1d4ed8' }}
                                                    />
                                                </BalanceBox>
                                            </Grid>
                                        )}

                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                label="Advance Amount"
                                                type="number"
                                                value={amount}
                                                onChange={(e) => setAmount(e.target.value)}
                                                InputProps={{ startAdornment: <InputAdornment position="start">Rs.</InputAdornment> }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <CustomNepaliDatePicker
                                                label="Received Date"
                                                value={date}
                                                onChange={(nextDate) => setDate(nextDate)}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                label="Note"
                                                value={note}
                                                onChange={(e) => setNote(e.target.value)}
                                                placeholder="e.g. Advance for upcoming months"
                                                multiline
                                                rows={2}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Stack direction="row" justifyContent="flex-end">
                                                <Button
                                                    variant="contained"
                                                    startIcon={saving ? <SaveIcon /> : <AddIcon />}
                                                    onClick={handleSave}
                                                    disabled={saving}
                                                    sx={{ borderRadius: '12px', px: 4, py: 1.3, textTransform: 'none', fontWeight: 800 }}
                                                >
                                                    {saving ? 'Saving...' : 'Save Advance'}
                                                </Button>
                                            </Stack>
                                        </Grid>

                                        <Grid item xs={12}>
                                            <Divider sx={{ my: 1 }} />
                                            <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1.5 }}>
                                                Recent Advance History
                                            </Typography>
                                            <HistoryPanel>
                                                {!selectedStudent ? (
                                                    <Typography variant="body2" color="textSecondary" sx={{ py: 3, textAlign: 'center' }}>
                                                        Select a student to view advance history.
                                                    </Typography>
                                                ) : transactions.length === 0 ? (
                                                    <Typography variant="body2" color="textSecondary" sx={{ py: 3, textAlign: 'center' }}>
                                                        No advance transactions yet.
                                                    </Typography>
                                                ) : (
                                                    <List disablePadding>
                                                        {transactions.slice(0, 12).map((txn) => (
                                                            <ListItemButton key={txn._id || `${txn.type}-${txn.createdAt}-${txn.amount}`} sx={{ borderRadius: '10px', mb: 0.75 }}>
                                                                <ListItemText
                                                                    primary={
                                                                        <Stack direction="row" justifyContent="space-between" spacing={2}>
                                                                            <Typography variant="body2" sx={{ fontWeight: 800 }}>
                                                                                {txn.type === 'credit' ? 'Advance added' : 'Advance deducted'}
                                                                            </Typography>
                                                                            <Typography variant="body2" sx={{ fontWeight: 900, color: txn.type === 'credit' ? '#16a34a' : '#dc2626' }}>
                                                                                {txn.type === 'credit' ? '+' : '-'} Rs.{(Number(txn.amount) || 0).toLocaleString()}
                                                                            </Typography>
                                                                        </Stack>
                                                                    }
                                                                    secondary={`${txn.date ? formatDatePart(txn.date) : 'No date'} | ${txn.note || 'No note'} | Balance: Rs.${(Number(txn.balanceAfter) || 0).toLocaleString()}`}
                                                                />
                                                            </ListItemButton>
                                                        ))}
                                                    </List>
                                                )}
                                            </HistoryPanel>
                                        </Grid>
                                    </Grid>
                                )}
                            </Box>
                        </StyledPaper>
                    </Grid>
                </Grid>
            </Container>
        </PageContainer>
    );
};

export default AdvanceFees;

const PageContainer = styled.div`background: #f8fafc; min-height: 100vh; animation: ${fadeIn} 0.5s ease-out;`;
const HeaderWrapper = styled(Paper)`
    background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
    border-radius: 0 0 40px 40px !important;
    padding-bottom: 60px;
`;
const StyledPaper = styled(Paper)`
    border-radius: 20px !important;
    overflow: hidden;
    height: 100%;
    border: 1px solid #e2e8f0;
    display: flex;
    flex-direction: column;
`;
const SectionHeader = styled(Box)`
    background: #f8fafc;
    padding: 16px 24px;
    border-bottom: 1px solid #e2e8f0;
    display: flex;
    align-items: center;
    gap: 12px;
    color: #1e293b;
    svg { color: #2563eb; }
`;
const ClassItem = styled(Button)`
    width: 100%;
    justify-content: flex-start !important;
    text-align: left !important;
    padding: 12px 16px !important;
    margin-bottom: 8px !important;
    border-radius: 12px !important;
    text-transform: none !important;
    font-weight: 600 !important;
    color: ${props => props.$active ? '#fff' : '#64748b'} !important;
    background: ${props => props.$active ? 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)' : 'transparent'} !important;
    border: 1px solid ${props => props.$active ? 'transparent' : '#e2e8f0'} !important;

    &:hover {
        background: ${props => props.$active ? 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)' : '#f1f5f9'} !important;
        transform: translateX(4px);
    }

    svg {
        margin-right: 12px;
        color: ${props => props.$active ? '#fff' : '#2563eb'};
    }
`;
const BalanceBox = styled(Box)`
    padding: 20px;
    border-radius: 14px;
    border: 1px solid #bfdbfe;
    background: #eff6ff;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
    flex-wrap: wrap;
`;
const HistoryPanel = styled(Box)`
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 14px;
    padding: 10px;
    max-height: 330px;
    overflow-y: auto;
`;
